import { useCallback, useEffect, useRef } from 'react';
import { Outlet } from 'react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { CFT, useSentrySetCft, PIPE } from '@repo/shared-kit/sentry';
import { useSegment } from '@repo/poly-hooks/segment';
import { kycQueries, KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycContext } from '../context';
import { shouldSubmitKyc } from '../utils';

/**
 * Guard for KYC routes.
 * Checks if KYC should be submitted (required but not yet submitted).
 * If KYC should be submitted, renders child routes.
 * If KYC should not be submitted (already accepted or submitted), redirects via onComplete callback.
 * This guard is the same for all KYC routes - the redirect logic is handled by onComplete.
 *
 * @example
 * ```tsx
 * // In app router:
 * <Route element={<KycGuard onComplete={handleComplete} />}>
 *   <Route path="poi" element={<OwnerPoiPage />} />
 * </Route>
 * ```
 */
export function KycAccessGuard() {
  const { onComplete, isRegisterFlow, legalCountry, membershipId, trackingPrefix } =
    useKycContext();
  const market = isRegisterFlow ? legalCountry : undefined;
  const { data: kycInfo } = useSuspenseQuery(kycQueries.item(market));
  const cancelled = useRef(false);
  const segment = useSegment();

  useSentrySetCft({ cft: CFT.DUE_DILIGENCE, pipe: PIPE.DUE_DILIGENCE.INITIAL });

  const submitKyc = shouldSubmitKyc(kycInfo);

  const trackSkipKyc = useCallback(() => {
    segment.track(`${trackingPrefix}_kyc_skipped`, {
      membership_id: membershipId,
      provider: KycProvider.PROVIDER_UNSPECIFIED,
    });
  }, [segment, trackingPrefix, membershipId]);

  useEffect(() => {
    if (!submitKyc && !cancelled.current) {
      trackSkipKyc();
      onComplete(KycProvider.PROVIDER_UNSPECIFIED);
    }
  }, [submitKyc, onComplete, trackSkipKyc]);

  useEffect(() => {
    return () => {
      cancelled.current = true;
    };
  }, []);

  if (!submitKyc) {
    return null;
  }

  return <Outlet />;
}
